import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCalcLeite } from '../../screens/Calcleite/CalcleiteContext';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import './Producao.css';
import Config from '../../config/config.json'; 
import tags from '../../config/tags.json';

// --- FUNÇÃO AUXILIAR PARA FORMATAÇÃO ---
const formatValueForDisplay = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const number = Number(String(value).replace(/\D/g, '')) / 100;
  if (isNaN(number)) return '';
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);
};

const Producao = () => {
  const { dados, setDados } = useCalcLeite();
  const navigate = useNavigate();
  const [mostrarResultados, setMostrarResultados] = useState(false);

  useEffect(() => {
    if (!dados.producao) {
      setDados(prev => ({
        ...prev,
        producao: {
          leiteProduzido: '', areaPastos: '', areaVolumosos: '',
          vacasRebanho: '', vacasLactacao: '', colaboradores: '',
          dataSelecionada: new Date().toISOString().split('T')[0],
        }
      }));
    }
    if (dados.resultadosProducao) {
      setMostrarResultados(true);
    }
  }, [dados.producao, dados.resultadosProducao, setDados]);

  const handleNumericChange = (e) => {
    const { name, value } = e.target;
    const rawValue = value.replace(/\D/g, '');
    setDados(prev => ({
      ...prev,
      producao: { ...prev.producao, [name]: rawValue }
    }));
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDados(prev => ({
      ...prev,
      producao: { ...prev.producao, [name]: value }
    }));
  };

  const handleCalcular = () => {
    const p = dados.producao;
    if (!p || !p.leiteProduzido) {
      alert("Por favor, preencha o campo 'Litros de Leite Produzido'.");
      return;
    }

    const leiteProduzido = (Number(p.leiteProduzido) || 0) / 100;
    const areaPastos = (Number(p.areaPastos) || 0) / 100;
    const areaVolumosos = (Number(p.areaVolumosos) || 0) / 100;
    const vacasRebanho = (Number(p.vacasRebanho) || 0) / 100;
    const vacasLactacao = (Number(p.vacasLactacao) || 0) / 100;
    const colaboradores = (Number(p.colaboradores) || 0) / 100;

    const resultados = {
      leitePastagem: areaPastos ? (leiteProduzido / areaPastos).toFixed(2) : "0.00",
      leiteVolumoso: areaVolumosos ? (leiteProduzido / areaVolumosos).toFixed(2) : "0.00",
      leiteVacaRebanho: vacasRebanho ? (leiteProduzido / vacasRebanho / 30).toFixed(2) : "0.00",
      leiteVacaLactacao: vacasLactacao ? (leiteProduzido / vacasLactacao / 30).toFixed(2) : "0.00",
      relacaoLactacaoRebanho: vacasRebanho ? ((vacasLactacao / vacasRebanho) * 100).toFixed(2) : "0.00",
      leiteColaborador: colaboradores ? (leiteProduzido / colaboradores / 30).toFixed(2) : "0.00",
    };

    setDados(prev => ({ ...prev, resultadosProducao: resultados }));
    setMostrarResultados(true);
  };
  
  const handleDownloadPDF = () => {
    const p = dados.producao;
    const r = dados.resultadosProducao;

    if (!r) {
        alert("Calcule os resultados primeiro para gerar o PDF.");
        return;
    }

    const doc = new jsPDF();
    
    // Cabeçalho com fundo azul
    doc.setFillColor(30, 144, 255); // #1e90ff
    doc.rect(0, 0, 210, 40, 'F'); // Cabeçalho com 40mm de altura
    doc.setTextColor(255, 255, 255); // Texto branco
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text("Relatório de Produção e Produtividade", 105, 20, { align: 'center' });

    // Data centralizada abaixo do título
    doc.setTextColor(200, 200, 200); // Cinza claro
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    const dataFormatada = new Date(p.dataSelecionada).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
    doc.text(`Data: ${dataFormatada}`, 105, 30, { align: 'center' });

    // Adicionar imagens no cabeçalho com posicionamento ajustado
    const custosAgroImg = Config.colors.base64.custosagro;
    const appImg = Config.colors.base64[tags.tags.app];
    if (custosAgroImg) {
      doc.addImage(custosAgroImg, 'PNG', 10, 7.5, 25, 25); // Logo CustosAgro mais à esquerda
    }
    if (appImg) {
      doc.addImage(appImg, 'PNG', 175, 7.5, 25, 25); // Logo do app mais à direita
    }

    // Tabela de Dados Inseridos
    doc.setTextColor(80, 80, 80);
    doc.setFontSize(11);
    const dadosInseridosBody = [
        ['Litros de Leite Produzido', `${formatValueForDisplay(p.leiteProduzido)} L`],
        ['Área de Pastos', `${formatValueForDisplay(p.areaPastos)} ha`],
        ['Área de Volumosos', `${formatValueForDisplay(p.areaVolumosos)} ha`],
        ['Vacas no Rebanho', `${formatValueForDisplay(p.vacasRebanho)} cab`],
        ['Vacas em Lactação', `${formatValueForDisplay(p.vacasLactacao)} cab`],
        ['Nº de Colaboradores', `${formatValueForDisplay(p.colaboradores)}`],
    ];

    autoTable(doc, {
        startY: 50,
        head: [['Descrição', 'Valor Inserido']],
        body: dadosInseridosBody,
        theme: 'striped',
        headStyles: { fillColor: [44, 62, 80] },
    });

    // Tabela de Resultados
    const resultadosBody = [
        ['Leite / Hectare de Pasto', `${r.leitePastagem} L/ha`],
        ['Leite / Hectare de Volumoso', `${r.leiteVolumoso} L/ha`],
        ['Leite / Vaca no Rebanho / Dia', `${r.leiteVacaRebanho} L/cab/dia`],
        ['Leite / Vaca em Lactação / Dia', `${r.leiteVacaLactacao} L/cab/dia`],
        ['Relação Lactação/Rebanho', `${r.relacaoLactacaoRebanho} %`],
        ['Leite / Colaborador / Dia', `${r.leiteColaborador} L/colab/dia`],
    ];

    autoTable(doc, {
        head: [['Indicador de Produtividade', 'Resultado Calculado']],
        body: resultadosBody,
        theme: 'grid',
        headStyles: { fillColor: [30, 144, 255] },
    });

    // Adicionar numeração de páginas
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(9);
        doc.setTextColor(150, 150, 150);
        doc.text(`Relatório gerado por CustosAgro - Página ${i} de ${pageCount}`, 105, 287, { align: 'center' });
    }
    
    doc.save("relatorio_producao_calcleite.pdf");
  };

  const handleLimparDados = () => {
    if(window.confirm("Tem certeza que deseja limpar todos os dados desta tela?")) {
        setDados(prev => {
            const { producao, resultadosProducao, ...resto } = prev;
            localStorage.setItem('calcLeiteData', JSON.stringify(resto));
            return resto;
        });
        setMostrarResultados(false);
    }
  };

  return (
    <div className="producao-container">
      <div className="producao-card">
        <h1>Produção e Produtividade</h1>
        <p className="producao-subtitle">Preencha os campos para calcular os indicadores.</p>

        <div className="producao-form">
          <div className="producao-input-group">
            <label>Data da Análise</label>
            <input type="date" name="dataSelecionada" value={dados.producao?.dataSelecionada || ''} onChange={handleDateChange} />
          </div>
          <div className="producao-input-group">
            <label>Litros de Leite Produzido</label>
            <input type="text" name="leiteProduzido" placeholder="Ex: 15.000,00" value={formatValueForDisplay(dados.producao?.leiteProduzido)} onChange={handleNumericChange} />
          </div>
          <div className="producao-input-group">
            <label>Área de Pastos (hectares)</label>
            <input type="text" name="areaPastos" placeholder="Ex: 50,00" value={formatValueForDisplay(dados.producao?.areaPastos)} onChange={handleNumericChange} />
          </div>
          <div className="producao-input-group">
            <label>Área de Volumosos (hectares)</label>
            <input type="text" name="areaVolumosos" placeholder="Ex: 10,00" value={formatValueForDisplay(dados.producao?.areaVolumosos)} onChange={handleNumericChange} />
          </div>
          <div className="producao-input-group">
            <label>Vacas no Rebanho (cab)</label>
            <input type="text" name="vacasRebanho" placeholder="Ex: 100,00" value={formatValueForDisplay(dados.producao?.vacasRebanho)} onChange={handleNumericChange} />
          </div>
          <div className="producao-input-group">
            <label>Vacas em Lactação (cab)</label>
            <input type="text" name="vacasLactacao" placeholder="Ex: 85,00" value={formatValueForDisplay(dados.producao?.vacasLactacao)} onChange={handleNumericChange} />
          </div>
          <div className="producao-input-group">
            <label>Nº de Colaboradores</label>
            <input type="text" name="colaboradores" placeholder="Ex: 4,00" value={formatValueForDisplay(dados.producao?.colaboradores)} onChange={handleNumericChange} />
          </div>
          <button className="producao-button" onClick={handleCalcular}>Calcular</button>
        </div>

        {mostrarResultados && (
          <div className="producao-results">
            <h2>Resultados</h2>
            <ul>
              <li>Leite / Hectare de Pasto: <strong>{dados.resultadosProducao?.leitePastagem} L/ha</strong></li>
              <li>Leite / Hectare de Volumoso: <strong>{dados.resultadosProducao?.leiteVolumoso} L/ha</strong></li>
              <li>Leite / Vaca no Rebanho / Dia: <strong>{dados.resultadosProducao?.leiteVacaRebanho} L/cab/dia</strong></li>
              <li>Leite / Vaca em Lactação / Dia: <strong>{dados.resultadosProducao?.leiteVacaLactacao} L/cab/dia</strong></li>
              <li>Relação Lactação/Rebanho: <strong>{dados.resultadosProducao?.relacaoLactacaoRebanho} %</strong></li>
              <li>Leite / Colaborador / Dia: <strong>{dados.resultadosProducao?.leiteColaborador} L/colab/dia</strong></li>
            </ul>
            <button className="producao-button pdf" onClick={handleDownloadPDF}>Baixar Relatório (PDF)</button>
          </div>
        )}

        <div className="producao-navigation">
          <button className="producao-button clean" onClick={handleLimparDados}>Limpar Dados</button>
          <button className="producao-button navigate" onClick={() => navigate('/calcleite/app/capital')}>Avançar para Capital</button>
        </div>
      </div>
    </div>
  );
};

export default Producao;